import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Language } from '../translations';
import { Expedition, Category } from '../types';
import AysenMap from '../components/AysenMap';
import { InstagramFeed } from '../components/InstagramFeed';
import { 
    Compass, 
    Mountain, 
    ShieldCheck, 
    Radio, 
    ArrowUpRight, 
    Sparkles, 
    Clock, 
    Gauge, 
    Flame, 
    ChevronRight,
    MapPin,
    Eye,
    Zap
} from 'lucide-react';

interface HomeAProps {
    t: any;
    lang: Language;
    expeditions?: Expedition[];
    homeSections?: any[];
}

export const HomeA: React.FC<HomeAProps> = ({ t, lang, expeditions = [], homeSections }) => {
    // Parallax & Mouse tracking states
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [scrollY, setScrollY] = useState(0);
    const [activePerspective, setActivePerspective] = useState<'urban' | 'mountain'>('mountain');
    const [selectedCategory, setSelectedCategory] = useState<Category>('all');
    const heroRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            const { innerWidth, innerHeight } = window;
            const normX = (e.clientX / innerWidth - 0.5) * 2; // -1 to 1
            const normY = (e.clientY / innerHeight - 0.5) * 2; // -1 to 1
            setMousePos({ x: normX, y: normY });
        };

        const handleScroll = () => {
            setScrollY(window.scrollY);
        };

        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Filter expeditions
    const filteredExpeditions = expeditions.filter(exp => {
        if (selectedCategory === 'all') return true;
        return exp.category === selectedCategory;
    }).slice(0, 6);

    // Scroll progress for hero opening effect (0 to 1 over first 600px)
    const splitProgress = Math.min(scrollY / 600, 1);

    return (
        <div className="bg-[#0b0d11] text-[#ededed] overflow-x-hidden selection:bg-[#00d4ff] selection:text-black">
            
            {/* =========================================================================
                1. HERO SECTION: "EL PORTAL DE LA FISURA" (3D Multilayer Parallax)
               ========================================================================= */}
            <section 
                ref={heroRef}
                className="relative h-screen w-full min-h-[720px] overflow-hidden flex items-center justify-center bg-black"
            >
                {/* LAYER 1: Deep Mountain Background with subtle counter-parallax */}
                <div 
                    className="absolute inset-0 z-0 will-change-transform transition-transform duration-300 ease-out"
                    style={{
                        transform: `scale(1.12) translate(${mousePos.x * -12}px, ${mousePos.y * -8}px) translateY(${scrollY * 0.25}px)`
                    }}
                >
                    <img
                        src="https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Laguna%20Cerro%20Castillo/trekking-laguna-cerro-castillo-turquesa.jpg"
                        alt="Cerro Castillo Fissure Landscape"
                        className="w-full h-full object-cover brightness-[0.7] contrast-[1.1]"
                    />
                    {/* Atmospheric shadows and dark vignette */}
                    <div className="absolute inset-0 bg-radial-gradient from-transparent via-black/40 to-black/90" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0b0d11] via-black/30 to-black/70" />
                </div>

                {/* LAYER 2: Central Glacial Rift Light (The "Luz en la Grieta") */}
                <div 
                    className="absolute inset-0 z-10 pointer-events-none flex items-center justify-center"
                    style={{
                        opacity: 0.8 + Math.sin(Date.now() / 1000) * 0.1,
                        transform: `translate(${mousePos.x * 10}px, ${mousePos.y * 10}px)`
                    }}
                >
                    {/* Cyan vertical beam radiating through the crack */}
                    <div className="w-[120px] md:w-[260px] h-full bg-gradient-to-r from-transparent via-[#00d4ff]/25 to-transparent blur-3xl transform -rotate-6" />
                    <div className="absolute w-[2px] h-full bg-gradient-to-b from-transparent via-[#00d4ff] to-transparent opacity-60 shadow-[0_0_30px_#00d4ff]" />
                </div>

                {/* LAYER 3: FOREGROUND CLIFFS / PAREDES DE ROCA QUE SE ABREN CON EL SCROLL */}
                {/* Left Granite Wall */}
                <div 
                    className="absolute top-0 bottom-0 left-0 w-1/3 md:w-1/4 z-20 pointer-events-none transition-transform duration-500 ease-out"
                    style={{
                        transform: `translateX(-${splitProgress * 90}%) translate(${mousePos.x * 18}px, ${mousePos.y * 8}px)`
                    }}
                >
                    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full fill-[#07090c] opacity-95 drop-shadow-2xl">
                        <polygon points="0,0 75,0 45,28 65,52 35,78 70,100 0,100" />
                    </svg>
                    {/* Subtle rock edge glow */}
                    <div className="absolute right-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-[#00d4ff]/40 to-transparent blur-[1px]" />
                </div>

                {/* Right Granite Wall */}
                <div 
                    className="absolute top-0 bottom-0 right-0 w-1/3 md:w-1/4 z-20 pointer-events-none transition-transform duration-500 ease-out"
                    style={{
                        transform: `translateX(${splitProgress * 90}%) translate(${mousePos.x * -18}px, ${mousePos.y * -8}px)`
                    }}
                >
                    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full fill-[#07090c] opacity-95 drop-shadow-2xl">
                        <polygon points="100,0 25,0 55,32 30,58 60,82 25,100 100,100" />
                    </svg>
                    {/* Subtle rock edge glow */}
                    <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-[#00d4ff]/40 to-transparent blur-[1px]" />
                </div>

                {/* Telemetry Mountain HUD (Top Right) */}
                <div className="absolute top-24 md:top-28 right-6 md:right-12 z-30 hidden sm:flex flex-col items-end gap-1 font-mono text-[11px] text-gray-400 bg-black/60 backdrop-blur-md px-3.5 py-2.5 rounded-lg border border-white/10 shadow-lg">
                    <div className="flex items-center gap-1.5 text-[#00d4ff]">
                        <Compass className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: '20s' }} />
                        <span className="font-bold tracking-widest">AYSÉN TELEMETRY</span>
                    </div>
                    <div className="text-gray-300">46°03′00″ S &bull; 72°11′00″ W</div>
                    <div className="text-[10px] text-gray-400">CERRO CASTILLO: 2.675 M.S.N.M.</div>
                    <div className="flex items-center gap-2 mt-1 pt-1 border-t border-white/10 text-[10px]">
                        <span className="text-emerald-400 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                            ROCA SECA
                        </span>
                        <span>&bull;</span>
                        <span>OCT &mdash; ABR</span>
                    </div>
                </div>

                {/* HERO CONTENT: Titular Editorial y Manifiesto Central */}
                <div className="relative z-30 max-w-5xl mx-auto px-6 text-center space-y-8 pt-16">
                    {/* Concept Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-xs font-mono uppercase tracking-[0.25em] text-[#00d4ff] shadow-inner">
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>Propuesta A &bull; Parallax Geológico Multicapa</span>
                    </div>

                    {/* Massive Punchy Title */}
                    <div className="space-y-3">
                        <h1 className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-title uppercase tracking-tight text-white leading-[0.92] drop-shadow-[0_10px_30px_rgba(0,0,0,0.9)]">
                            Una fisura no es una rotura.
                            <br />
                            <span className="bg-gradient-to-r from-white via-[#e0f7fc] to-[#00d4ff] bg-clip-text text-transparent">
                                Es el inicio de la cumbre.
                            </span>
                        </h1>
                        <p className="max-w-2xl mx-auto text-base sm:text-lg md:text-xl text-gray-300 font-light leading-relaxed font-sans pt-2">
                            En una pared infranqueable de granito, la fisura es el único camino que permite el ascenso. 
                            En la Patagonia profunda, es la grieta por donde entra la aventura.
                        </p>
                    </div>

                    {/* Interactive CTAs */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                        <a
                            href="#expediciones"
                            className="group relative inline-flex items-center gap-3 px-8 py-4 bg-[#00d4ff] text-black font-title uppercase tracking-widest text-sm font-bold rounded-lg overflow-hidden transition-all duration-300 hover:bg-white hover:shadow-[0_0_30px_rgba(0,212,255,0.6)]"
                        >
                            <span>Explorar Rutas en la Roca</span>
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>

                        <a
                            href="#resignificacion"
                            className="inline-flex items-center gap-2 px-8 py-4 bg-white/5 hover:bg-white/15 border border-white/20 text-white font-title uppercase tracking-widest text-sm rounded-lg backdrop-blur-md transition-all duration-300 hover:border-[#00d4ff]/60"
                        >
                            <Eye className="w-4 h-4 text-[#00d4ff]" />
                            <span>El Giro Conceptual</span>
                        </a>
                    </div>

                    {/* Scroll Down Indicator */}
                    <div className="pt-8 flex flex-col items-center gap-2 text-gray-400 text-xs font-mono tracking-widest uppercase animate-bounce">
                        <span>Haz scroll para abrir la fisura</span>
                        <div className="w-5 h-8 rounded-full border-2 border-white/30 flex items-start justify-center p-1">
                            <div className="w-1 h-2 bg-[#00d4ff] rounded-full animate-pulse" />
                        </div>
                    </div>
                </div>

                {/* Bottom Gradient Fade */}
                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0b0d11] to-transparent z-20 pointer-events-none" />
            </section>


            {/* =========================================================================
                2. SECCIÓN: "LA RESIGNIFICACIÓN DE LA FISURA" (Interactive Paradigm Switch)
               ========================================================================= */}
            <section id="resignificacion" className="relative py-28 px-6 max-w-7xl mx-auto space-y-16">
                <div className="text-center max-w-3xl mx-auto space-y-4">
                    <span className="text-[11px] font-mono uppercase tracking-[0.4em] text-[#00d4ff] block">
                        Storytelling &bull; Cambio de Paradigma
                    </span>
                    <h2 className="text-3xl sm:text-5xl font-title uppercase tracking-tight text-white">
                        Dos formas de mirar una grieta
                    </h2>
                    <p className="text-gray-400 font-light text-base sm:text-lg">
                        El mundo urbano suele ver la fisura como un fallo o una imperfección. 
                        En la montaña, nosotros la vemos como nuestra mayor aliada.
                    </p>

                    {/* Interactive Toggle Switch */}
                    <div className="pt-4 inline-flex p-1.5 bg-black/60 border border-white/10 rounded-xl backdrop-blur-md">
                        <button
                            onClick={() => setActivePerspective('urban')}
                            className={`px-5 py-2 rounded-lg text-xs font-title uppercase tracking-wider transition-all duration-300 ${
                                activePerspective === 'urban'
                                    ? 'bg-red-500/20 text-red-300 border border-red-500/40 shadow-lg'
                                    : 'text-gray-400 hover:text-white'
                            }`}
                        >
                            La Mirada Común
                        </button>
                        <button
                            onClick={() => setActivePerspective('mountain')}
                            className={`px-5 py-2 rounded-lg text-xs font-title uppercase tracking-wider transition-all duration-300 ${
                                activePerspective === 'mountain'
                                    ? 'bg-[#00d4ff]/20 text-[#00d4ff] border border-[#00d4ff]/40 shadow-[0_0_20px_rgba(0,212,255,0.2)]'
                                    : 'text-gray-400 hover:text-white'
                            }`}
                        >
                            La Mirada Fisura Expeditions
                        </button>
                    </div>
                </div>

                {/* 3 Pillars of the Fissure */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                    {/* Pillar 1 */}
                    <div className="relative group p-8 rounded-2xl bg-[#11151c]/80 border border-white/10 hover:border-[#00d4ff]/50 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.6)]">
                        <div className="flex items-center justify-between mb-6">
                            <span className="font-mono text-xs text-[#00d4ff] tracking-widest">01 // ESCALADA</span>
                            <div className="p-2.5 rounded-lg bg-[#00d4ff]/10 text-[#00d4ff]">
                                <Mountain className="w-5 h-5" />
                            </div>
                        </div>

                        <h3 className="text-2xl font-title uppercase tracking-wide text-white mb-3">
                            La Línea de Vida
                        </h3>

                        <p className="text-sm text-gray-300 font-light leading-relaxed mb-6">
                            {activePerspective === 'mountain' ? (
                                <span>
                                    En una pared de granito vertical de 600 metros, la fisura es el <strong>único punto donde encaja la mano</strong>, 
                                    la bota y la leva de seguridad. No es una falla: es la ruta que permite tocar la cima.
                                </span>
                            ) : (
                                <span className="text-gray-400 italic">
                                    "Una fractura en la estructura. Una señal de peligro que debe ser sellada o evitada."
                                </span>
                            )}
                        </p>

                        <div className="pt-4 border-t border-white/10 flex items-center justify-between text-xs font-mono text-gray-400">
                            <span>CRACK CLIMBING</span>
                            <span className="text-[#00d4ff]">PROTECCIÓN PURA</span>
                        </div>
                    </div>

                    {/* Pillar 2 */}
                    <div className="relative group p-8 rounded-2xl bg-[#11151c]/80 border border-white/10 hover:border-[#00d4ff]/50 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.6)]">
                        <div className="flex items-center justify-between mb-6">
                            <span className="font-mono text-xs text-[#00d4ff] tracking-widest">02 // GLACIOLOGÍA</span>
                            <div className="p-2.5 rounded-lg bg-[#00d4ff]/10 text-[#00d4ff]">
                                <Sparkles className="w-5 h-5" />
                            </div>
                        </div>

                        <h3 className="text-2xl font-title uppercase tracking-wide text-white mb-3">
                            La Revelación Interior
                        </h3>

                        <p className="text-sm text-gray-300 font-light leading-relaxed mb-6">
                            {activePerspective === 'mountain' ? (
                                <span>
                                    Cuando el hielo de Aysén se fisura, no se rompe: <strong>desvela el azul turquesa más puro de la Tierra</strong>. 
                                    Una ventana de agua milenaria oculta bajo la superficie durante millones de años.
                                </span>
                            ) : (
                                <span className="text-gray-400 italic">
                                    "Un quiebre frío e inestable en la masa de hielo."
                                </span>
                            )}
                        </p>

                        <div className="pt-4 border-t border-white/10 flex items-center justify-between text-xs font-mono text-gray-400">
                            <span>TURQUESA GLACIAR</span>
                            <span className="text-[#00d4ff]">VENTANA AL PASADO</span>
                        </div>
                    </div>

                    {/* Pillar 3 */}
                    <div className="relative group p-8 rounded-2xl bg-[#11151c]/80 border border-white/10 hover:border-[#00d4ff]/50 backdrop-blur-xl transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.6)]">
                        <div className="flex items-center justify-between mb-6">
                            <span className="font-mono text-xs text-[#00d4ff] tracking-widest">03 // EXISTENCIAL</span>
                            <div className="p-2.5 rounded-lg bg-[#00d4ff]/10 text-[#00d4ff]">
                                <Zap className="w-5 h-5" />
                            </div>
                        </div>

                        <h3 className="text-2xl font-title uppercase tracking-wide text-white mb-3">
                            Por Donde Entra la Luz
                        </h3>

                        <p className="text-sm text-gray-300 font-light leading-relaxed mb-6">
                            {activePerspective === 'mountain' ? (
                                <span>
                                    <em>«Hay una grieta en todo, y así es como entra la luz»</em> (Leonard Cohen). 
                                    Venir a Cerro Castillo es abrir una fisura en el estrés urbano para reconectar con lo salvaje.
                                </span>
                            ) : (
                                <span className="text-gray-400 italic">
                                    "Una ruptura incómoda de la rutina diaria establecida."
                                </span>
                            )}
                        </p>

                        <div className="pt-4 border-t border-white/10 flex items-center justify-between text-xs font-mono text-gray-400">
                            <span>DESCONEXIÓN PURA</span>
                            <span className="text-[#00d4ff]">LIBERTAD</span>
                        </div>
                    </div>
                </div>
            </section>


            {/* =========================================================================
                3. SECCIÓN: EXPEDICIONES EN CAPAS GEOLÓGICAS (Depth Cards)
               ========================================================================= */}
            <section id="expediciones" className="relative py-24 px-6 max-w-7xl mx-auto space-y-12">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <span className="text-[11px] font-mono uppercase tracking-[0.4em] text-[#00d4ff] block mb-2">
                            Catálogo Técnico &bull; Temporada 2026-2027
                        </span>
                        <h2 className="text-3xl sm:text-5xl font-title uppercase tracking-tight text-white">
                            Expediciones en el Granito
                        </h2>
                    </div>

                    {/* Category Filter Pills */}
                    <div className="flex flex-wrap gap-2">
                        {[
                            { id: 'all', label: 'Todas las Rutas' },
                            { id: 'trekking', label: 'Trekking' },
                            { id: 'climbing', label: 'Escalada' },
                            { id: 'water', label: 'Agua & Hielo' },
                        ].map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id as Category)}
                                className={`px-4 py-2 rounded-lg text-xs font-title uppercase tracking-wider transition-all duration-300 ${
                                    selectedCategory === cat.id
                                        ? 'bg-[#00d4ff] text-black font-bold shadow-[0_0_15px_rgba(0,212,255,0.4)]'
                                        : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                                }`}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Grid of Depth Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {filteredExpeditions.map((exp, idx) => {
                        const name = exp[`name_${lang}`] || exp.name_es || exp.name_en;
                        const catchphrase = exp[`catchphrase_${lang}`] || exp.catchphrase_es || '';
                        const price = exp.price_from || exp.price_flat;

                        return (
                            <Link
                                key={exp.id || idx}
                                to={`/expeditions/${exp.slug}`}
                                className="group relative rounded-2xl overflow-hidden bg-[#11151c] border border-white/10 hover:border-[#00d4ff] transition-all duration-500 flex flex-col justify-between hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
                            >
                                {/* Image Container with Parallax Zoom */}
                                <div className="relative h-64 w-full overflow-hidden">
                                    <img
                                        src={exp.image}
                                        alt={name}
                                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 brightness-90 group-hover:brightness-100"
                                    />
                                    {/* Gradient overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#11151c] via-[#11151c]/30 to-transparent" />

                                    {/* Category tag */}
                                    <div className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-[10px] font-mono uppercase tracking-widest text-[#00d4ff]">
                                        {exp.category}
                                    </div>

                                    {/* Difficulty Badge */}
                                    <div className="absolute top-4 right-4 z-10 px-2.5 py-1 rounded-md bg-black/60 backdrop-blur-md border border-white/10 text-[10px] font-mono text-gray-300 flex items-center gap-1">
                                        <Gauge className="w-3 h-3 text-[#00d4ff]" />
                                        <span>{exp.difficulty === 'high' ? 'Exigente' : exp.difficulty === 'mid' ? 'Media-Alta' : 'Accesible'}</span>
                                    </div>
                                </div>

                                {/* Content Details */}
                                <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
                                    <div>
                                        <h3 className="text-xl sm:text-2xl font-title uppercase tracking-wide text-white group-hover:text-[#00d4ff] transition-colors line-clamp-1">
                                            {name}
                                        </h3>
                                        {catchphrase && (
                                            <p className="text-xs text-gray-400 font-light mt-1 line-clamp-2 italic">
                                                "{catchphrase}"
                                            </p>
                                        )}
                                    </div>

                                    {/* Tech specs row */}
                                    <div className="grid grid-cols-2 gap-2 pt-3 border-t border-white/10 text-xs font-mono text-gray-400">
                                        <div className="flex items-center gap-1.5">
                                            <Clock className="w-3.5 h-3.5 text-[#00d4ff]" />
                                            <span>{exp.duration}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5 justify-end">
                                            <MapPin className="w-3.5 h-3.5 text-[#00d4ff]" />
                                            <span>Villa Cerro Castillo</span>
                                        </div>
                                    </div>

                                    {/* Price and CTA */}
                                    <div className="pt-2 flex items-center justify-between">
                                        <div>
                                            <span className="text-[10px] text-gray-400 block font-mono uppercase">Inversión</span>
                                            <span className="text-lg font-title font-bold text-white">
                                                {price ? `$${price} USD` : 'A consultar'}
                                            </span>
                                        </div>

                                        <span className="inline-flex items-center gap-1 text-xs font-title uppercase tracking-wider text-[#00d4ff] group-hover:translate-x-1 transition-transform">
                                            <span>Ver Ficha</span>
                                            <ChevronRight className="w-4 h-4" />
                                        </span>
                                    </div>
                                </div>

                                {/* Fissure Glow line on bottom */}
                                <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#00d4ff] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                            </Link>
                        );
                    })}
                </div>

                {/* View All Button */}
                <div className="text-center pt-8">
                    <Link
                        to="/expeditions"
                        className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg border border-white/20 hover:border-[#00d4ff] bg-white/5 hover:bg-white/10 text-white font-title uppercase tracking-widest text-xs transition-all duration-300"
                    >
                        <span>Ver Catálogo Completo ({expeditions.length} Rutas)</span>
                        <ChevronRight className="w-4 h-4 text-[#00d4ff]" />
                    </Link>
                </div>
            </section>


            {/* =========================================================================
                4. SECCIÓN: SEGURIDAD Y GUÍAS ANGM (Pared de Confianza)
               ========================================================================= */}
            <section className="py-24 bg-gradient-to-b from-[#0b0d11] via-[#111622] to-[#0b0d11] border-y border-white/10">
                <div className="max-w-7xl mx-auto px-6 space-y-16">
                    <div className="text-center max-w-3xl mx-auto space-y-4">
                        <span className="text-[11px] font-mono uppercase tracking-[0.4em] text-[#00d4ff] block">
                            Excelencia Técnica &bull; Cero Azar
                        </span>
                        <h2 className="text-3xl sm:text-5xl font-title uppercase tracking-tight text-white">
                            Guiados por quienes habitan la roca
                        </h2>
                        <p className="text-gray-300 font-light text-base sm:text-lg">
                            No somos intermediarios de agencia. Nacimos y vivimos en Villa Cerro Castillo, 
                            con certificaciones de clase mundial y protocolos de rescate activos.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="p-8 rounded-xl bg-black/40 border border-white/10 backdrop-blur-md space-y-4">
                            <div className="w-12 h-12 rounded-lg bg-[#00d4ff]/10 text-[#00d4ff] flex items-center justify-center">
                                <ShieldCheck className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-title uppercase tracking-wider text-white">
                                Acreditación ANGM & WFR
                            </h3>
                            <p className="text-sm text-gray-400 font-light leading-relaxed">
                                Asociación Nacional de Guías de Montaña de Chile y primeros auxilios en áreas silvestres agrestes.
                            </p>
                        </div>

                        <div className="p-8 rounded-xl bg-black/40 border border-white/10 backdrop-blur-md space-y-4">
                            <div className="w-12 h-12 rounded-lg bg-[#00d4ff]/10 text-[#00d4ff] flex items-center justify-center">
                                <Radio className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-title uppercase tracking-wider text-white">
                                Enlace Satelital InReach
                            </h3>
                            <p className="text-sm text-gray-400 font-light leading-relaxed">
                                Comunicación bidireccional y geolocalización satelital en tiempo real durante cada jornada en terreno.
                            </p>
                        </div>

                        <div className="p-8 rounded-xl bg-black/40 border border-white/10 backdrop-blur-md space-y-4">
                            <div className="w-12 h-12 rounded-lg bg-[#00d4ff]/10 text-[#00d4ff] flex items-center justify-center">
                                <Flame className="w-6 h-6" />
                            </div>
                            <h3 className="text-xl font-title uppercase tracking-wider text-white">
                                Grupos Íntimos & Sin Rastro
                            </h3>
                            <p className="text-sm text-gray-400 font-light leading-relaxed">
                                Máximo 6 exploradores por guía para garantizar seguridad técnica, respeto al ecosistema e inmersión pura.
                            </p>
                        </div>
                    </div>
                </div>
            </section>


            {/* =========================================================================
                5. MAPA DE AYSÉN Y CAMPO BASE
               ========================================================================= */}
            <div className="py-20 px-6 max-w-7xl mx-auto space-y-8">
                <div className="text-center space-y-3">
                    <span className="text-[11px] font-mono uppercase tracking-[0.4em] text-[#00d4ff] block">
                        Cartografía Viva
                    </span>
                    <h2 className="text-3xl sm:text-4xl font-title uppercase tracking-tight text-white">
                        Territorio Aysén: Donde Nacen las Rutas
                    </h2>
                </div>
                <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                    <AysenMap expeditions={expeditions} />
                </div>
            </div>


            {/* =========================================================================
                6. INSTAGRAM & CIERRE CINEMATOGRÁFICO
               ========================================================================= */}
            <section className="py-24 bg-[#07090c] border-t border-white/10">
                <div className="max-w-7xl mx-auto px-6 space-y-12">
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                        <div>
                            <span className="text-[10px] font-mono uppercase tracking-[0.4em] text-[#00d4ff] block mb-2">
                                Diario Visual de Montaña
                            </span>
                            <h3 className="text-2xl sm:text-3xl font-title uppercase tracking-tight text-white">
                                Sigue la Expedición en Tiempo Real
                            </h3>
                        </div>
                        <a
                            href="https://www.instagram.com/fisuraexpeditions/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-mono text-gray-400 hover:text-[#00d4ff] uppercase tracking-widest border-b border-white/20 pb-1 transition-colors"
                        >
                            @fisuraexpeditions
                        </a>
                    </div>
                    <InstagramFeed />
                </div>
            </section>
        </div>
    );
};

export default HomeA;
